import AppBaseScreen from "@toothfairy/shared-ui/AppBaseScreen";
import AppTab from "@toothfairy/shared-ui/AppTab";
import AppToast from "@toothfairy/shared-ui/AppToast";
import React from "react";
import { View } from "react-native";
import { useTranslation } from "react-i18next";
import useWorkspaces from "../../Hooks/useWorkspaces";
import CostingView from "./Components/CostingView";
import ModelView from "./Components/ModelView";
import ChatAnalyticsView from "./Components/ChatAnalyticsView";
import ConsumptionByUserView from "./Components/ConsumptionByUserView";
import TrainingConsumptionView from "./Components/TrainingConsumptionView";
import SupportConsumptionChart from "./Components/SupportConsumptionChart";

const DashboardScreen = ({
  labels,
  datasets,
  selectedDate,
  changeMonthByUnit,
  consumptionAPIInProgress,
  costAPIInProgress,
  parsingCost,
  trainingCost,
  nersBreakDown,
  intentsBreakDown,
  topicsBreakDown,
  documentsBreakdown,
  trainingDuration,
  trainingAPIInProgress,
  initialTabFromRouting,
  batchConsumption,
  costChart,
  normalisedConsumption,
  normalisedTrainingConsumption,
  totalPredictionCost,
  totalTrainingCost,
  isAdmin,
  displayedAgents,
  chatAnalyticsData,
  chatAnalyticsAPIInProgress,
  agentsByMode,
  agentFunctionsByType,
  filterStartDate,
  filterEndDate,
  filterAgentId,
  onFilterStartDateChange,
  onFilterEndDateChange,
  onFilterAgentChange,
  onExportChats,
  rawChatsCount,
  consumptionByType,
  consumptionByModel,
  consumptionLogs,
  consumptionLogsLoading,
  totalUoIConsumed,
  cacheUsage,
  billingPeriod,
  agents,
  agentsFunctions,
  execEnvs,
  authorisations,
  mrcDocuments,
  aiEntitiesConfig,
  jobs,
  triggers,
}) => {
  const { handleTabSelection, getLastTabSelectedByScreen } = useWorkspaces();
  const { t } = useTranslation();
  const routes = [{ key: "second", title: t("dashboard.tabs.overview"), routeId: "model" }];
  if (isAdmin) {
    routes.push({ key: "first", title: t("dashboard.tabs.usage"), routeId: "usage" });
    routes.push({ key: "third", title: t("dashboard.tabs.analytics"), routeId: "analytics" });
  }

  const tabIndexFromRouting = routes
    .map((u) => u.routeId)
    .indexOf(initialTabFromRouting);
  const AppToasty = AppToast.useToast();
  const _initialIndex =
    tabIndexFromRouting > -1
      ? tabIndexFromRouting
      : getLastTabSelectedByScreen("dashboard");

  const costs = () => (
    <>
      <CostingView
        labels={labels}
        consumptionAPIInProgress={consumptionAPIInProgress}
        costAPIInProgress={costAPIInProgress}
        datasets={datasets}
        parsingCost={parsingCost}
        trainingCost={trainingCost}
        trainingDuration={trainingDuration}
        selectedDate={selectedDate}
        changeMonthByUnit={changeMonthByUnit}
        batchConsumption={batchConsumption}
        costChart={costChart}
        normalisedConsumption={normalisedConsumption}
        normalisedTrainingConsumption={normalisedTrainingConsumption}
        totalPredictionCost={totalPredictionCost}
        totalTrainingCost={totalTrainingCost}
        displayedAgents={displayedAgents}
        consumptionByType={consumptionByType}
        consumptionByModel={consumptionByModel}
        totalUoIConsumed={totalUoIConsumed}
        cacheUsage={cacheUsage}
        billingPeriod={billingPeriod}
      />
      <SupportConsumptionChart
        billingPeriod={billingPeriod}
        selectedDate={selectedDate}
      />
      <ConsumptionByUserView
        billingPeriod={billingPeriod}
        selectedDate={selectedDate}
        logs={consumptionLogs}
        isFetchInProgress={consumptionLogsLoading}
      />
      <TrainingConsumptionView
        consumptionAPIInProgress={consumptionAPIInProgress}
        isParseCost={
          normalisedConsumption?.datasets?.length > 0 || datasets?.length > 0
        }
        normalisedTrainingConsumption={normalisedTrainingConsumption}
      />
    </>
  );

  const model = () => (
    <ModelView
      intentsBreakDown={intentsBreakDown}
      nersBreakDown={nersBreakDown}
      topicsBreakDown={topicsBreakDown}
      documentsBreakdown={documentsBreakdown}
      trainingAPIInProgress={trainingAPIInProgress}
      agentsByMode={agentsByMode}
      agentFunctionsByType={agentFunctionsByType}
      agents={agents}
      agentsFunctions={agentsFunctions}
      execEnvs={execEnvs}
      authorisations={authorisations}
      mrcDocuments={mrcDocuments}
      aiEntitiesConfig={aiEntitiesConfig}
      jobs={jobs}
      triggers={triggers}
    />
  );

  const analytics = () => (
    <ChatAnalyticsView
      chatAnalyticsData={chatAnalyticsData}
      chatAnalyticsAPIInProgress={chatAnalyticsAPIInProgress}
      agents={displayedAgents}
      filterStartDate={filterStartDate}
      filterEndDate={filterEndDate}
      filterAgentId={filterAgentId}
      onFilterStartDateChange={onFilterStartDateChange}
      onFilterEndDateChange={onFilterEndDateChange}
      onFilterAgentChange={onFilterAgentChange}
      onExportChats={onExportChats}
      rawChatsCount={rawChatsCount}
    />
  );

  return (
    <AppBaseScreen>
      <View style={{ paddingHorizontal: 20, paddingBottom: 10 }}>
        <AppTab
          nonAnimatedTabViewStyle={{
            paddingBottom: 0,
          }}
          initialIndex={_initialIndex}
          onTabChange={(route) => {
            handleTabSelection(route, routes, "dashboard");
          }}
          renderSceneObject={{
            second: model,
            first: costs,
            third: analytics,
          }}
          routes={routes}
        />
      </View>
    </AppBaseScreen>
  );
};

export default DashboardScreen;
